import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { JustTCGCardData } from '../../services/justTcgService';

interface PriceHistoryChartProps {
  pricingData: JustTCGCardData;
}

export const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({ pricingData }) => {
  const theme = useTheme();
  const [chartPeriod, setChartPeriod] = useState<'7d' | '30d' | '90d' | '180d'>('7d');

  // Don't render if no price history data
  if (!pricingData.priceHistory7d && !pricingData.priceHistory30d && 
      !pricingData.priceHistory90d && !pricingData.priceHistory180d) {
    return null;
  }

  // Get the appropriate data based on selected period
  const chartData = (
    chartPeriod === '7d' ? pricingData.priceHistory7d :
    chartPeriod === '30d' ? pricingData.priceHistory30d :
    chartPeriod === '90d' ? pricingData.priceHistory90d :
    pricingData.priceHistory180d
  )?.map(item => ({
    date: new Date(item.timestamp * 1000).toLocaleDateString(),
    price: item.price
  }));

  return (
    <Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha(theme.palette.background.paper, 0.5), borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Price History</Typography>
        <ToggleButtonGroup
          value={chartPeriod}
          exclusive
          onChange={(e, newPeriod) => newPeriod && setChartPeriod(newPeriod)}
          size="small"
        >
          {pricingData.priceHistory7d && pricingData.priceHistory7d.length >= 2 && (
            <ToggleButton value="7d">7 Days</ToggleButton>
          )}
          {pricingData.priceHistory30d && 
           pricingData.priceHistory30d.length > (pricingData.priceHistory7d?.length || 0) && (
            <ToggleButton value="30d">30 Days</ToggleButton>
          )}
          {pricingData.priceHistory90d && 
           pricingData.priceHistory90d.length > (pricingData.priceHistory30d?.length || 0) && (
            <ToggleButton value="90d">90 Days</ToggleButton>
          )}
          {pricingData.priceHistory180d && 
           pricingData.priceHistory180d.length > (pricingData.priceHistory90d?.length || 0) && (
            <ToggleButton value="180d">180 Days</ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>
      
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }}
            stroke={theme.palette.text.secondary}
          />
          <YAxis 
            tick={{ fontSize: 10 }}
            stroke={theme.palette.text.secondary}
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4
            }}
            formatter={(value: number) => [`$${value.toFixed(2)}`, 'Price']}
          />
          <Line 
            type="monotone" 
            dataKey="price" 
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
